import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import retryStrategyService from '../services/retryStrategyService';

// Async thunks
export const fetchRetryStrategies = createAsyncThunk(
  'retryStrategies/fetchAll',
  async (params, { rejectWithValue }) => {
    try {
      return await retryStrategyService.getStrategies(params);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchDefaultStrategy = createAsyncThunk(
  'retryStrategies/fetchDefault',
  async (_, { rejectWithValue }) => {
    try {
      return await retryStrategyService.getDefault();
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const createRetryStrategy = createAsyncThunk(
  'retryStrategies/create',
  async (strategyData, { rejectWithValue }) => {
    try {
      return await retryStrategyService.createStrategy(strategyData);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateRetryStrategy = createAsyncThunk(
  'retryStrategies/update',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      return await retryStrategyService.updateStrategy(id, data);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteRetryStrategy = createAsyncThunk(
  'retryStrategies/delete',
  async (id, { rejectWithValue }) => {
    try {
      await retryStrategyService.deleteStrategy(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const duplicateRetryStrategy = createAsyncThunk(
  'retryStrategies/duplicate',
  async (id, { rejectWithValue }) => {
    try {
      return await retryStrategyService.duplicateStrategy(id);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const toggleRetryStrategyActive = createAsyncThunk(
  'retryStrategies/toggleActive',
  async (id, { rejectWithValue }) => {
    try {
      return await retryStrategyService.toggleActive(id);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  strategies: [],
  defaultStrategy: null,
  loading: false,
  error: null,
  selectedStrategy: null
};

const retryStrategySlice = createSlice({
  name: 'retryStrategies',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setSelectedStrategy: (state, action) => {
      state.selectedStrategy = action.payload;
    },
    clearSelectedStrategy: (state) => {
      state.selectedStrategy = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch all strategies
      .addCase(fetchRetryStrategies.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRetryStrategies.fulfilled, (state, action) => {
        state.loading = false;
        state.strategies = action.payload;
      })
      .addCase(fetchRetryStrategies.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fetch default strategy
      .addCase(fetchDefaultStrategy.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDefaultStrategy.fulfilled, (state, action) => {
        state.loading = false;
        state.defaultStrategy = action.payload;
      })
      .addCase(fetchDefaultStrategy.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Create strategy
      .addCase(createRetryStrategy.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createRetryStrategy.fulfilled, (state, action) => {
        state.loading = false;
        state.strategies.push(action.payload);
      })
      .addCase(createRetryStrategy.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Update strategy
      .addCase(updateRetryStrategy.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateRetryStrategy.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.strategies.findIndex(s => s.id === action.payload.id);
        if (index !== -1) {
          state.strategies[index] = action.payload;
        }
        // Update default strategy if it was updated
        if (state.defaultStrategy && state.defaultStrategy.id === action.payload.id) {
          state.defaultStrategy = action.payload;
        }
      })
      .addCase(updateRetryStrategy.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Delete strategy
      .addCase(deleteRetryStrategy.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteRetryStrategy.fulfilled, (state, action) => {
        state.loading = false;
        state.strategies = state.strategies.filter(s => s.id !== action.payload);
        // Clear selected strategy if it was deleted
        if (state.selectedStrategy === action.payload) {
          state.selectedStrategy = null;
        }
      })
      .addCase(deleteRetryStrategy.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Duplicate strategy
      .addCase(duplicateRetryStrategy.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(duplicateRetryStrategy.fulfilled, (state, action) => {
        state.loading = false;
        state.strategies.push(action.payload);
      })
      .addCase(duplicateRetryStrategy.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Toggle active
      .addCase(toggleRetryStrategyActive.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(toggleRetryStrategyActive.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.strategies.findIndex(s => s.id === action.payload.id);
        if (index !== -1) {
          state.strategies[index] = action.payload;
        }
        // Update default strategy if it was toggled
        if (state.defaultStrategy && state.defaultStrategy.id === action.payload.id) {
          state.defaultStrategy = action.payload;
        }
      })
      .addCase(toggleRetryStrategyActive.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { clearError, setSelectedStrategy, clearSelectedStrategy } = retryStrategySlice.actions;

// Selectors
export const selectRetryStrategies = (state) => state.retryStrategies.strategies;
export const selectDefaultRetryStrategy = (state) => state.retryStrategies.defaultStrategy;
export const selectRetryStrategiesLoading = (state) => state.retryStrategies.loading;
export const selectRetryStrategiesError = (state) => state.retryStrategies.error;
export const selectSelectedRetryStrategy = (state) => state.retryStrategies.selectedStrategy;
export const selectActiveRetryStrategies = (state) => state.retryStrategies.strategies.filter(s => s.is_active);

export default retryStrategySlice.reducer; 